<template>
    <themed-modal
        :show="true"
        size="large"
        @close="$emit('close')"
    >
        <template #header>
            <h3>{{ getTitle }}</h3>
        </template>

        <template #body>
            <div class="media-viewer">
                <!-- Photo Viewer -->
                <div v-if="item.type === 'photo'" class="photo-viewer">
                    <img :src="item.file_url" :alt="item.caption || 'Photo'" />
                </div>

                <!-- Video Viewer -->
                <div v-else-if="item.type === 'video'" class="video-viewer">
                    <video controls :src="item.file_url">
                        Your browser does not support the video tag.
                    </video>
                </div>

                <!-- Note Viewer -->
                <div v-else-if="item.type === 'note'" class="note-viewer">
                    <div v-if="!isEditing" class="note-content" v-html="formattedContent"></div>
                    <textarea 
                        v-else
                        v-model="editedContent"
                        rows="15"
                        class="note-editor"
                    ></textarea>
                </div>

                <!-- Media Info -->
                <div class="media-info">
                    <div class="info-row">
                        <label>Caption:</label>
                        <themed-input
                            v-if="isEditingCaption"
                            v-model="editedCaption"
                            placeholder="Add caption..."
                        />
                        <div v-else class="caption-display">
                            {{ item.caption || 'No caption' }}
                        </div>
                    </div>

                    <div class="info-row">
                        <label>Author:</label>
                        <span>{{ getAuthorName }}</span>
                    </div>

                    <div class="info-row">
                        <label>Date:</label>
                        <span>{{ formatDate(item.captured_at || item.created_at) }}</span>
                    </div>

                    <div v-if="item.tour_comp" class="info-row">
                        <label>Property:</label>
                        <span>{{ getCompName }}</span>
                    </div>

                    <div v-if="item.type !== 'note' && item.file_size" class="info-row">
                        <label>File Size:</label>
                        <span>{{ item.formatted_file_size }}</span>
                    </div>

                    <div v-if="item.metadata && item.metadata.dimensions" class="info-row">
                        <label>Dimensions:</label>
                        <span>{{ item.metadata.dimensions.width }} × {{ item.metadata.dimensions.height }}</span>
                    </div>
                </div>
            </div>
        </template>

        <template #footer>
            <div class="viewer-actions">
                <div class="left-actions">
                    <themed-button 
                        v-if="canEdit && !isEditing && !isEditingCaption"
                        variant="secondary"
                        @click="startEdit"
                    >
                        <theme-icon icon="edit" type="icons" />
                        Edit
                    </themed-button>

                    <themed-button 
                        v-if="isEditing || isEditingCaption"
                        variant="primary"
                        @click="saveChanges"
                        :disabled="isSaving"
                    >
                        <span v-if="isSaving">Saving...</span>
                        <span v-else>Save Changes</span>
                    </themed-button>

                    <themed-button 
                        v-if="isEditing || isEditingCaption"
                        variant="secondary"
                        @click="cancelEdit"
                    >
                        Cancel
                    </themed-button>
                </div>

                <div class="right-actions">
                    <themed-button 
                        v-if="item.type !== 'note'"
                        variant="secondary"
                        @click="downloadMedia"
                    >
                        <theme-icon icon="download" type="icons" />
                        Download
                    </themed-button>

                    <themed-button 
                        v-if="canDelete"
                        variant="danger"
                        @click="deleteMedia"
                    >
                        <theme-icon icon="trash" type="icons" />
                        Delete
                    </themed-button>

                    <themed-button variant="secondary" @click="$emit('close')">
                        Close
                    </themed-button>
                </div>
            </div>
        </template>
    </themed-modal>
</template>

<script>
import ThemedModal from '../ThemedModal.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemedInput from '../ThemedInput.vue';
import ThemeIcon from '../ThemeIcon.vue';
import moment from 'moment';

export default {
    name: 'TourMediaViewer',

    components: {
        ThemedModal,
        ThemedButton,
        ThemedInput,
        ThemeIcon,
    },

    props: {
        item: {
            type: Object,
            required: true,
        },
        tourId: {
            type: Number,
            required: true,
        },
    },

    data() {
        return {
            isEditing: false,
            isEditingCaption: false,
            editedCaption: this.item.caption || '',
            editedContent: this.item.content || '',
            isSaving: false,
        };
    },

    computed: {
        getTitle() {
            if (this.item.type === 'photo') return 'Photo';
            if (this.item.type === 'video') return 'Video';
            if (this.item.type === 'note') return this.item.caption || 'Note';
            return 'Media';
        },

        canEdit() {
            return this.item.user_id === window.userId;
        },

        canDelete() {
            return this.item.user_id === window.userId;
        },

        getAuthorName() {
            if (this.item.user) {
                return this.item.user.name;
            }
            if (this.item.attendee) {
                return this.item.attendee.attendee_name || this.item.attendee.attendee_email;
            }
            return 'Unknown';
        },

        getCompName() {
            if (!this.item.tour_comp) return '';
            return this.item.tour_comp.comp?.building?.name || 
                   this.item.tour_comp.building?.name || 
                   'Unknown Property';
        },

        formattedContent() {
            if (!this.item.content) return '';
            // Convert line breaks to <br> tags
            return this.item.content.replace(/\n/g, '<br>');
        },
    },

    methods: {
        startEdit() {
            if (this.item.type === 'note') {
                this.isEditing = true;
                this.editedContent = this.item.content || '';
            } else {
                this.isEditingCaption = true;
                this.editedCaption = this.item.caption || '';
            }
        },

        cancelEdit() {
            this.isEditing = false;
            this.isEditingCaption = false;
            this.editedCaption = this.item.caption || '';
            this.editedContent = this.item.content || '';
        },

        async saveChanges() {
            this.isSaving = true;

            try {
                let response;

                if (this.item.type === 'note' && this.isEditing) {
                    response = await axios.put(
                        `/tours/${this.tourId}/media/${this.item.id}/note`,
                        {
                            content: this.editedContent,
                            caption: this.item.caption,
                        }
                    );
                } else if (this.isEditingCaption) {
                    response = await axios.put(
                        `/tours/${this.tourId}/media/${this.item.id}/caption`,
                        {
                            caption: this.editedCaption,
                        }
                    );
                }

                this.$notify({
                    type: 'success',
                    title: 'Success',
                    text: 'Changes saved successfully',
                });

                this.$emit('updated', response.data);
                this.isEditing = false;
                this.isEditingCaption = false;
            } catch (error) {
                console.error('Failed to save changes:', error);
                this.$notify({
                    type: 'error',
                    title: 'Error',
                    text: 'Failed to save changes',
                });
            } finally {
                this.isSaving = false;
            }
        },

        async deleteMedia() {
            if (!confirm('Are you sure you want to delete this media?')) {
                return;
            }

            try {
                await axios.delete(`/tours/${this.tourId}/media/${this.item.id}`);
                
                this.$notify({
                    type: 'success',
                    title: 'Deleted',
                    text: 'Media deleted successfully',
                });

                this.$emit('deleted', this.item.id);
            } catch (error) {
                console.error('Failed to delete media:', error);
                this.$notify({
                    type: 'error',
                    title: 'Error',
                    text: 'Failed to delete media',
                });
            }
        },

        downloadMedia() {
            if (this.item.file_url) {
                window.open(this.item.file_url, '_blank');
            }
        },

        formatDate(date) {
            return moment(date).format('MMMM D, YYYY [at] h:mm A');
        },
    },
};
</script>
